import { ClickAwayListener, Fade, Paper, Popper } from "@mui/material";
import { StaticDateTimePicker } from "@mui/x-date-pickers";
import { DateTimeField } from "@mui/x-date-pickers/DateTimeField";
import clsx from "clsx";
import dayjs, { type Dayjs } from "dayjs";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type FocusEvent,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  useHubRideActionConfirm,
  useRideEmptyStationsAllowed,
  useRidesActions,
  useRidesListActions,
  useRtl,
} from "@ryde/redux";
import {
  applyIndependentRideDateChange,
  errors,
  formatDayjsToTimeHHmm,
  getUTCDateOrNowInISO,
  HubRideActionKind,
  isValidDateTime,
  Log,
  RideFormFields,
  rideStartEndChangeHandler,
  rideStartEndPositionAtom,
  rideStartEndPositionSelector,
  RideStationPanelMode,
  useFormats,
  useSystemParams,
  useTimezone,
  warnAndToastSimpleResult,
  type TRideDtoStartEndKey,
} from "@ryde/shared";

import { CustomMuiPickersActionBar } from "../../../../controls";
import { RideStartEndDateEditableAdditionStyled } from "./ride-start-end-date-editable-addition.styled";

const POPOVER_Y_OFFSET = 10;

export const RideStartEndDateEditableAddition: FC = () => {
  const { t } = useTranslation();
  const dateTimeRef = useRef<HTMLInputElement | null>(null);
  const { timezone } = useTimezone();
  const { allowShiftTimeOverrideDuringManualAction } = useSystemParams();
  const { updateRideStartEndDate } = useRidesActions();
  const { confirmHubRideAction } = useHubRideActionConfirm();
  const { dateTimeDelimitedFormat, isAMPM, timeFormat } = useFormats();
  const { rides } = useRidesListActions();
  const { isRtl } = useRtl();
  const { y, x, isFieldOpened, fieldName, rideId, iconAnchorElement } =
    useRecoilValue(rideStartEndPositionSelector);

  const setRideStartEndPosition = useSetRecoilState(rideStartEndPositionAtom);
  const rideDto = useMemo(
    () => rides?.find(r => r.id === rideId),
    [rideId, rides],
  );

  const { isRideHasIndependentDayAdjustment } = useRideEmptyStationsAllowed(
    RideStationPanelMode.GRID_PANEL,
    rideDto,
  );

  const [value, setValue] = useState<Dayjs | null>(null);

  const confirmHubModify = useEvent(async (): Promise<boolean> => {
    if (!rideDto) {
      return false;
    }

    return confirmHubRideAction({
      rides: [rideDto],
      actionKind: HubRideActionKind.Modify,
    });
  });

  useEffect(() => {
    if (!rideDto || !fieldName) {
      return;
    }

    const parsed = dayjs.utc(rideDto[fieldName]);
    setValue(isValidDateTime(parsed) ? parsed : null);
  }, [fieldName, iconAnchorElement, isFieldOpened, rideDto]);

  const setDate = async (valueUtc: dayjs.Dayjs | null): Promise<void> => {
    const updatedRideDateTime = getUTCDateOrNowInISO(valueUtc, timezone);

    if (!rideId) {
      Log.warnAndToast(t(errors.ride.invalidRideId));
      return;
    }

    if (!isValidDateTime(valueUtc)) {
      Log.warnAndToast(t(errors.ride.invalidRideDate));
      return;
    }

    if (!isRideHasIndependentDayAdjustment) {
      const result = rideStartEndChangeHandler({
        rideDto,
        updatedRideDateTime,
        rideStartEndKey: fieldName as TRideDtoStartEndKey,
        timeFormat,
        timezone,
        allowShiftTimeOverrideDuringManualAction,
      });

      if (result === null) {
        return;
      } else if (result === "revert") {
        return;
      }

      if (!(await confirmHubModify())) {
        return;
      }

      const dateUpdateResult = await updateRideStartEndDate({
        rideId,
        rideStartDate:
          fieldName === RideFormFields.rideStart
            ? getUTCDateOrNowInISO(valueUtc, timezone)
            : result.newOppositeStartEndRideValue,
        rideEndDate:
          fieldName === RideFormFields.rideEnd
            ? getUTCDateOrNowInISO(valueUtc, timezone)
            : result.newOppositeStartEndRideValue,
        rideTypeId: rideDto?.rideTypeId,
        metadata: {
          ...(rideDto?.metadata || {}),
          ...result.metadataUpdated,
        },
        isReturnDtoForGrid: true,
      });

      Log.debug(
        "Updating ride date ",
        rideId,
        formatDayjsToTimeHHmm(valueUtc, timezone),
      );

      if (dateUpdateResult.success) {
        Log.debug("Date updated successfully");
      } else {
        warnAndToastSimpleResult(dateUpdateResult, t);
      }

      return;
    }

    Log.debug(
      "Updating ride date with independent date adjustments ",
      rideId,
      valueUtc,
    );

    const independentDateChange = applyIndependentRideDateChange({
      rideDto: rideDto || {},
      updatedRideDateTime,
      rideStartEndKey: fieldName as TRideDtoStartEndKey,
    });

    if (!(await confirmHubModify())) {
      return;
    }

    const dateIndependentUpdateResult = await updateRideStartEndDate({
      rideId,
      rideStartDate: independentDateChange.rideStart,
      rideEndDate: independentDateChange.rideEnd,
      metadata: independentDateChange.metadata,
      rideTypeId: rideDto?.rideTypeId,
      isReturnDtoForGrid: true,
    });

    Log.debug("Updating ride date ", rideId, valueUtc);

    if (dateIndependentUpdateResult.success) {
      Log.debug("Date updated successfully");
    } else {
      warnAndToastSimpleResult(dateIndependentUpdateResult, t);
    }
  };

  const onBlur = async (event: FocusEvent<HTMLDivElement>): Promise<void> => {
    if (
      event?.relatedTarget?.classList.contains(
        "MuiPickersInputBase-sectionContent",
      )
    ) {
      return;
    }

    if (!value || !isValidDateTime(value)) {
      Log.warn("No valid value set to date time field");
      return;
    }

    const normalizedDateTimeFieldUtcValue = dayjs
      .tz(value, timezone)
      .startOf("minute")
      .utc();

    if (isValidDateTime(normalizedDateTimeFieldUtcValue)) {
      await setDate(normalizedDateTimeFieldUtcValue);
    }

    setRideStartEndPosition(prev => ({
      ...prev,
      x: 0,
      y: 0,
      isFieldOpened: false,
    }));
  };

  const onChange = (newValue: Dayjs | null): void => {
    setValue(newValue);
  };

  const handleClosePopover = useEvent((): void => {
    setRideStartEndPosition(prev => ({
      ...prev,
      x: prev.isFieldOpened ? prev.x : 0,
      y: prev.isFieldOpened ? prev.y : 0,
      iconAnchorElement: null,
    }));
  });

  useEffect(() => {
    if (!dateTimeRef.current) {
      return;
    }

    if (isFieldOpened) {
      const input = dateTimeRef.current?.querySelector("input");

      if (input) {
        input.focus();
      }
    }
  }, [isFieldOpened]);

  const closeEditors = useEvent(() => {
    if (isFieldOpened) {
      setRideStartEndPosition(prev => ({
        ...prev,
        isFieldOpened: false,
      }));
    }

    if (iconAnchorElement) {
      handleClosePopover();
    }
  });

  const prevIsRtl = useRef(isRtl);

  useEffect(() => {
    if (prevIsRtl.current !== isRtl) {
      closeEditors();
      prevIsRtl.current = isRtl;
    }
  }, [closeEditors, isRtl]);

  useEffect(() => {
    const scrollHandler = (event: Event): void => {
      const target = event.target as HTMLElement;
      const allowedClasses = ["grid_panel", "middle_scrollbar"];

      if (
        !target?.classList ||
        !allowedClasses.some(c => target.classList.contains(c))
      ) {
        return;
      }

      closeEditors();
    };

    document.addEventListener("scroll", scrollHandler, { capture: true });

    return () => {
      document.removeEventListener("scroll", scrollHandler, { capture: true });
    };
  }, [
    closeEditors,
    handleClosePopover,
    iconAnchorElement,
    isFieldOpened,
    setRideStartEndPosition,
  ]);

  return (
    <RideStartEndDateEditableAdditionStyled
      className={clsx("ride-start-end-date-editable-addition", {
        "ride-start-end-date-editable-addition--opened": isFieldOpened,
        "ride-start-end-date-editable-addition--ampm": isAMPM,
      })}
      style={{
        left: x,
        top: y,
      }}
    >
      <DateTimeField
        className="ride-start-end-date-editable-addition__date-time-field"
        dir={isRtl ? "rtl" : "ltr"}
        onBlur={onBlur}
        value={value}
        timezone={timezone}
        format={dateTimeDelimitedFormat}
        size="small"
        onChange={onChange}
        ref={dateTimeRef}
      />
      <Popper
        open={Boolean(iconAnchorElement)}
        anchorEl={iconAnchorElement as HTMLElement}
        placement={"bottom-start"}
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, POPOVER_Y_OFFSET],
              },
            },
          ],
        }}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper elevation={8}>
              <ClickAwayListener onClickAway={handleClosePopover}>
                <StaticDateTimePicker
                  value={
                    value !== null && isValidDateTime(value)
                      ? dayjs.utc(value)
                      : null
                  }
                  timezone={timezone}
                  onChange={setValue}
                  onAccept={setDate}
                  slots={{
                    actionBar: props => (
                      <CustomMuiPickersActionBar
                        customOnCancel={handleClosePopover}
                        customOnAccept={handleClosePopover}
                        {...props}
                      />
                    ),
                  }}
                  ampm={isAMPM}
                />
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </RideStartEndDateEditableAdditionStyled>
  );
};
